"use client";

import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { cn } from "@/lib/utils";

interface Testimonial {
  id: string;
  name: string;
  role?: string;
  content: string;
  avatarSrc: string;
}

interface Testimonial3DCardProps {
  testimonial: Testimonial;
  className?: string;
}

const Testimonial3DCard: React.FC<Testimonial3DCardProps> = ({
  testimonial,
  className,
}) => {
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);
  const [scale, setScale] = useState(1);
  const [glow, setGlow] = useState(false);
  
  const cardRef = useRef<HTMLDivElement>(null);
  
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    
    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    
    // Calculate mouse position relative to card center (in percentage)
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;
    
    // Convert to rotation degrees - max 10 degrees rotation
    const rotX = (mouseY / (rect.height / 2)) * -10; 
    const rotY = (mouseX / (rect.width / 2)) * 10;
    
    setRotateX(rotX);
    setRotateY(rotY);
    setScale(1.05);
    setGlow(true);
  };
  
  const handleMouseLeave = () => {
    setRotateX(0);
    setRotateY(0);
    setScale(1);
    setGlow(false);
  };
  
  return (
    <motion.div
      ref={cardRef}
      className={cn(
        "relative rounded-xl p-6 bg-white shadow-lg dark:bg-neutral-900 dark:border dark:border-emerald-500/10",
        "transition duration-300 ease-out cursor-pointer select-none",
        "w-full max-w-md aspect-[4/5]",
        className
      )}
      style={{
        transformStyle: "preserve-3d",
        willChange: "transform",
      }}
      animate={{
        rotateX: rotateX,
        rotateY: rotateY,
        scale: scale,
        boxShadow: glow 
          ? "0 10px 25px -5px rgba(4, 120, 87, 0.5), 0 8px 10px -6px rgba(4, 120, 87, 0.2)" 
          : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      }}
      transition={{ type: "spring", stiffness: 300, damping: 15 }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Sparkle glow effect in background */}
      {glow && (
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-emerald-500/20 via-transparent to-emerald-500/10 opacity-0 dark:opacity-100 rounded-xl" />
      )}
      
      {/* Content with 3D effect */}
      <div style={{ transform: "translateZ(20px)" }}>
        <div className="text-sm text-gray-500 dark:text-gray-300 italic mb-4 leading-relaxed">
          "{testimonial.content}"
        </div>
        
        <div className="flex items-center mt-6" style={{ transform: "translateZ(30px)" }}>
          <div className="relative h-12 w-12 overflow-hidden rounded-full border-2 border-emerald-500/50">
            <Image 
              src={testimonial.avatarSrc} 
              alt={testimonial.name}
              fill
              className="object-cover"
            />
          </div>
          <div className="ml-4">
            <h4 className="text-base font-semibold text-gray-900 dark:text-white">
              {testimonial.name}
            </h4>
            {testimonial.role && (
              <p className="text-sm text-gray-500 dark:text-gray-400">{testimonial.role}</p>
            )}
          </div>
        </div>
        
        {/* Geometric accents */}
        <div 
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30"
          style={{ transform: "translateZ(10px)" }} 
        />
        <div 
          className="absolute bottom-6 right-10 w-4 h-4 rounded-sm bg-emerald-200 dark:bg-emerald-800/30 rotate-12"
          style={{ transform: "translateZ(15px)" }} 
        />
      </div>
    </motion.div>
  );
};

// Container for multiple testimonial cards
export const Testimonial3DCardStack: React.FC<{
  testimonials: Testimonial[];
  className?: string;
}> = ({ testimonials, className }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [testimonials.length]);
  
  return (
    <div className={cn("relative w-full h-full", className)}>
      {testimonials.map((testimonial, index) => (
        <motion.div
          key={testimonial.id}
          className="absolute inset-0 flex items-center justify-center"
          initial={{ opacity: 0, scale: 0.8, rotateY: -20 }}
          animate={{
            opacity: index === currentIndex ? 1 : 0,
            scale: index === currentIndex ? 1 : 0.8,
            rotateY: index === currentIndex ? 0 : -20,
            zIndex: index === currentIndex ? 10 : 0,
          }}
          transition={{ 
            duration: 0.7,
            ease: [0.23, 1, 0.32, 1] 
          }}
        >
          <Testimonial3DCard testimonial={testimonial} />
        </motion.div>
      ))}
      
      {/* Navigation dots */}
      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {testimonials.map((_, index) => (
          <button
            key={index}
            className={cn(
              "w-2 h-2 rounded-full transition-all duration-300",
              index === currentIndex 
                ? "bg-emerald-500 w-4" 
                : "bg-emerald-200 dark:bg-emerald-800"
            )}
            onClick={() => setCurrentIndex(index)}
            aria-label={`Go to testimonial ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default Testimonial3DCard;
